import Img from '@components/Utility/Img';
import { ControllerType } from '@constants';
import { SiteControllerType } from '@features/sites/sitesAPI';
import CloudOffOutlinedIcon from '@mui/icons-material/CloudOffOutlined';
import Badge from '@mui/material/Badge';
import Box, { BoxProps } from '@mui/material/Box';
import Paper, { PaperProps } from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { lighten, styled } from '@mui/material/styles';
import { ElementType, useMemo } from 'react';
import { Link as RouterLink, To } from 'react-router-dom';

import AKSC255IMG from '@assets/ControllerImages/AKSC255.png';
import E2IMG from '@assets/ControllerImages/E2.png';
import E3IMG from '@assets/ControllerImages/E3.png';
import EVCOEV3224IMG from '@assets/ControllerImages/EVCO-EV3224.png';
import EVCO_CASE_MONITOR from '@assets/ControllerImages/EVCOCASEMONITOR.png';
import EVCOCONDENSINGUNIT from '@assets/ControllerImages/EVCOCondensingUnit.png';
import EVCOHGMMZ from '@assets/ControllerImages/EVCOHGMMZ.png';
import EVCOHGMSZ from '@assets/ControllerImages/EVCOHGMSZ.png';
import EVCOLIGHTINIMG from '@assets/ControllerImages/EVCOLighting.png';
import EVCOMONITORIMG from '@assets/ControllerImages/EVCOMonitor.png';
import EVCOPOWERMONITORIMG from '@assets/ControllerImages/EVCOPowerMonitor.png';
import EVCORTUIMG from '@assets/ControllerImages/EVCORTU.png';
import EVCOPUMPSTATIONIMG from '@assets/ControllerImages/EVCO_PUMP_STATION.png';
import LEO_AHU from '@assets/ControllerImages/LEO_AHU.png';
import LEO_CASE_MONITOR from '@assets/ControllerImages/LEO_CASE-MONITOR.png';
import LEO_CONDENSING_UNIT from '@assets/ControllerImages/LEO_CONDENSING-UNIT.png';
import LEO_HGMMZ from '@assets/ControllerImages/LEO_HGM-MZ.png';
import LEO_HGMSZ from '@assets/ControllerImages/LEO_HGM-SZ.png';
import LEO_LIGHTING from '@assets/ControllerImages/LEO_Lighting.png';
import LEO_RTU from '@assets/ControllerImages/LEO_RTU.png';
import SM800AIMG from '@assets/ControllerImages/SM800A.png';
import SM800SAIMG from '@assets/ControllerImages/SM800S.png';
import SM880IMG from '@assets/ControllerImages/SM880.png';
import SSIMG from '@assets/ControllerImages/SiteSupervisor.png';
import XR75CHIMG from '@assets/ControllerImages/XR75CH.png';
import HGMMZIMG from '@assets/ControllerImages/hgmmz.jpeg';
// import HEATCRAFTIMG from "@assets/ControllerImages/HEATCRAFT.jpeg"
import EVCOEV3273IMG from '@assets/ControllerImages/EV3273.png';
import NAIMG from '@assets/ControllerImages/Image_not_available.png';
import EVCOKILONODEIMG from '@assets/ControllerImages/KiloNode.png';
import LENNOXM4COREIMG from '@assets/ControllerImages/LENNOXM4CORE.png';
import SYMBIO800IMG from '@assets/ControllerImages/SYMBIO800.png';

type Props = {
  controller: SiteControllerType;
  controllersInAlarm: string[] | null;
  siteId: number;
  siteNameAndLocation: string;
};

type ControllerContainerProps = { inalarm: string } & PaperProps & {
    component: ElementType;
    to: To;
  };

function softNever(_x: never): undefined {
  return undefined;
}

function getControllerImage(controllerType: ControllerType) {
  switch (controllerType) {
    case ControllerType.E2:
      return E2IMG;
    case ControllerType.E3:
      return E3IMG;
    case ControllerType.SiteSupervisor:
    case ControllerType['Site Supervisor']:
      return SSIMG;
    case ControllerType.AKSC255:
      return AKSC255IMG;
    case ControllerType.EV3224:
      return EVCOEV3224IMG;
    case ControllerType.EV3273:
    case ControllerType.EV3283:
      return EVCOEV3273IMG;
    case ControllerType.EvcoMonitor:
    case ControllerType.LeoMonitor:
      return EVCOMONITORIMG;
    case ControllerType.EvcoPowerMonitor:
    case ControllerType.LeoPowerMonitor:
      return EVCOPOWERMONITORIMG;
    case ControllerType.KiloNode:
      return EVCOKILONODEIMG;
    case ControllerType.EvcoRtu:
    case ControllerType.EvcoRtuV2:
    case ControllerType.EvcoRtuZones:
    case ControllerType.EvcoRtuZonesV2:
      return EVCORTUIMG;
    case ControllerType.LeoRtu:
    case ControllerType.LeoRtuV2:
    case ControllerType.LeoRtuZones:
    case ControllerType.LeoRtuZonesV2:
      return LEO_RTU;
    case ControllerType.EvcoLighting:
    case ControllerType.EvcoLightingV2:
      return EVCOLIGHTINIMG;
    case ControllerType.LeoLighting:
    case ControllerType.LeoLightingV2:
      return LEO_LIGHTING;
    case ControllerType.EvcoPumpStation:
      return EVCOPUMPSTATIONIMG;
    case ControllerType.SM880:
    case ControllerType.SM880A:
      return SM880IMG;
    case ControllerType.SM800A:
      return SM800AIMG;
    case ControllerType.SM800SA:
      return SM800SAIMG;
    case ControllerType.XR75CH:
      return XR75CHIMG;
    case ControllerType.HGMMZ:
      return HGMMZIMG;
    case ControllerType.EvcoHgmMz:
      return EVCOHGMMZ;
    case ControllerType.EvcoHgmSz:
      return EVCOHGMSZ;
    case ControllerType.LeoHgmMz:
      return LEO_HGMMZ;
    case ControllerType.LeoHgmSz:
      return LEO_HGMSZ;
    case ControllerType.EVCO_CONDENSING_UNIT:
      return EVCOCONDENSINGUNIT;
    case ControllerType.LeoCondensingUnit:
      return LEO_CONDENSING_UNIT;
    case ControllerType.LennoxM4Core:
      return LENNOXM4COREIMG;
    case ControllerType.SYMBIO800:
      return SYMBIO800IMG;
    case ControllerType.EvcoAhu:
    case ControllerType.LeoAhu:
      return LEO_AHU;
    case ControllerType.EvcoCaseMonitor:
      return EVCO_CASE_MONITOR;
    case ControllerType.LeoCaseMonitor:
      return LEO_CASE_MONITOR;
    default:
      return softNever(controllerType);
  }
}

const ControllerContainer = styled(Paper)<ControllerContainerProps>(
  ({ theme, inalarm }) => ({
    display: 'flex',
    flexDirection: 'column',
    padding: 1,
    cursor: 'pointer',
    position: 'relative',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    border: `1px solid ${theme.palette.divider}`,
    minWidth: 100,
    minHeight: 100,
    flexShrink: 0,
    maxWidth: 300,
    borderRadius: theme.shape.borderRadius,
    '&:hover': {
      background: lighten(theme.palette.primary.main, 0.15),
    },
    ...(inalarm === 'true' && {
      backgroundColor: theme.palette.error.main,
      color: theme.palette.getContrastText(theme.palette.error.main),
      '&:hover': {
        backgroundColor: lighten(theme.palette.error.main, 0.15),
      },
    }),
  }),
);

type OfflineOverlayProps = BoxProps & {
  offline: string;
};
const OfflineOverlay = styled(Box)<OfflineOverlayProps>(
  ({ offline, theme }) => ({
    position: 'absolute',
    // boxSizing: "content-box",
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    borderWidth: 'inherit',
    borderStyle: 'inherit',
    borderColor: 'black',
    borderRadius: 'inherit',
    padding: 'inherit',
    display: 'none',
    justifyContent: 'center',
    alignItems: 'center',
    color: theme.palette.text.secondary,
    backgroundColor: 'rgba(125, 125, 125, 0.5)',
    ...(offline === 'true' && {
      display: 'flex',
      '&:hover': {
        backgroundColor: lighten('rgba(125, 125, 125, 0.5)', 0.15),
      },
    }),
  }),
);

const SiteController = ({
  controller,
  siteId,
  siteNameAndLocation,
  controllersInAlarm,
}: Props) => {
  const controllerUrl = useMemo(() => {
    return `/site/${siteId}/${encodeURIComponent(
      siteNameAndLocation,
    )}/controller/${encodeURIComponent(controller.controller_name)}`;
  }, [siteId, siteNameAndLocation, controller]);

  const imgSrc = useMemo(() => {
    if (controller.controller_type !== null) {
      const image = getControllerImage(
        (controller.controller_type as string).toLowerCase() as ControllerType,
      );
      if (image) return image;
    }
    return NAIMG;
  }, [controller.controller_type]);

  return (
    <ControllerContainer
      inalarm={
        controllersInAlarm
          ? `${controllersInAlarm.includes(controller.controller_name)}`
          : `${false}`
      }
      component={RouterLink}
      to={controllerUrl}
      elevation={6}
      aria-label="Navigate to Controller's Page"
    >
      <Badge
        invisible={controller.offline !== 1}
        showZero
        badgeContent={<CloudOffOutlinedIcon sx={{ color: 'grey' }} />}
      >
        <Box
          sx={{
            width: '100%',
            position: 'relative',
            minWidth: 240,
            textAlign: 'center',
          }}
        >
          <Img
            alt={`${controller.controller_type ?? 'Unknown Device'} Image`}
            src={imgSrc}
            sx={{
              position: 'relative',
              objectFit: 'contain',
              width: '100%',
              maxHeight: 200,
              backgroundColor: '#ccc',
            }}
          />
        </Box>
      </Badge>

      <Typography variant="h5" textAlign="center">
        {controller.controller_name}
      </Typography>
      <OfflineOverlay offline={(controller.offline === 1).toString()}>
        <CloudOffOutlinedIcon sx={{ fontSize: '3rem' }} />
      </OfflineOverlay>
    </ControllerContainer>
  );
};

export default SiteController;
